import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Celula } from '@/componentes/celula';
import { Falha } from '@/componentes/estado-da-tela';
import { RodapeFunil } from '@/componentes/rodape-funil';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Skeleton } from '@/componentes/ui/skeleton';
import type { ChaveMes, Integrador, PerfilMeses } from '@/dados';
import { mensagemDoErro, useIntegrador, useSalvarIntegrador } from '@/dados';
import { parseNum } from '@/design/formato';
import { Pagina } from '@/layout/pagina';

import { coberturaOferecida, type Cobertura } from './cobertura';
import { Historico } from './historico';
import { CHAVES, mesesVazios, type LinhaMes } from './meses';
import { resumirHistorico } from './resumo';

/* Sazonalidade: quantos projetos a empresa vende por ano.
 *
 * É o volume que o motor usa para ratear a despesa fixa, então esta tela
 * decide se o preço sugerido existe. Um caminho só, o histórico mês a mês,
 * gravado em `integradores.sazonalidade` {meses, cobertura}.
 *
 * Já houve um segundo caminho, a média de projetos por mês, gravada em
 * `integradores.projetos_mes`. Ele saiu porque respondia a mesma pergunta com
 * menos verdade: média não tem forma, e sem forma o ano é uma reta que nenhum
 * integrador vive. O motor continua lendo `projetos_mes` para quem gravou
 * antes (é a premissa 4, e nada no banco foi apagado), mas a tela não oferece
 * mais o campo e zera a coluna assim que um histórico é salvo: duas fontes de
 * volume vivas ao mesmo tempo foi o que fazia o número mudar sozinho.
 *
 * Histórico parcial é bem-vindo: informa-se a cobertura (quantos meses os
 * números representam) e o motor anualiza por soma dividida pela cobertura
 * vezes doze. O ticket médio NÃO anualiza: é média por projeto.
 *
 * Este módulo só decide O QUE mostrar e o que gravar; o formulário está em
 * `historico.tsx`, e a leitura sai de `resumo.ts`, o mesmo objeto que alimenta
 * a tela e o salvamento. */

export function Sazonalidade() {
  const integrador = useIntegrador();
  const salvar = useSalvarIntegrador();
  const navegar = useNavigate();

  const [meses, setMeses] = React.useState<Record<ChaveMes, LinhaMes>>(mesesVazios);
  const [cobertura, setCobertura] = React.useState<Cobertura | null>(null);

  const hidratado = React.useRef(false);

  const linha = integrador.data;
  React.useEffect(() => {
    // Erro não hidrata: o formulário não pode nascer vazio e depois recusar o
    // dado que chegar num refetch bem-sucedido.
    if (hidratado.current || integrador.isPending || integrador.isError) return;
    hidratado.current = true;

    const saz = linha?.sazonalidade ?? null;
    const remoto: PerfilMeses = saz?.meses ?? {};
    const carregados = mesesVazios();
    for (const chave of CHAVES) {
      const projetos = Math.round(parseNum(remoto[chave]?.projetos));
      const valor = parseNum(remoto[chave]?.valor);
      carregados[chave] = {
        projetos: projetos > 0 ? String(projetos) : '',
        valor: valor > 0 ? valor : null,
      };
    }
    setMeses(carregados);

    /* Cobertura ausente vale 12: é o dado antigo, gravado antes do campo
       existir. Cobertura fora dos cortes oferecidos hoje volta a ser `null`,
       e a pessoa escolhe de novo: os números digitados continuam aqui, mas a
       premissa que multiplica o ano inteiro tem de ser uma que ela declare. */
    if (saz?.meses) setCobertura(coberturaOferecida(parseNum(saz.cobertura) || 12));
  }, [integrador.isPending, integrador.isError, linha]);

  const resumo = resumirHistorico({
    meses,
    cobertura,
    integrador: linha ?? null,
  });

  function mudarMes(chave: ChaveMes, valores: LinhaMes) {
    setMeses((atuais) => ({ ...atuais, [chave]: valores }));
  }

  async function enviar() {
    /* Dado vazio não pode virar dado falso: N meses zerados NÃO são
       sazonalidade, e gravá-los faria o volume anual "existir" valendo zero.
       `projetos_mes` vai a nulo junto porque esta tela é a autoridade do
       volume: o que ela grava é o que vale. */
    const patch: Partial<Integrador> = {
      sazonalidade:
        cobertura !== null && resumo.temHistorico ? { meses: resumo.perfil, cobertura } : null,
      projetos_mes: null,
    };

    try {
      await salvar.mutateAsync(patch);
      navegar('/indicadores');
    } catch (erro) {
      toast.error(mensagemDoErro(erro));
    }
  }

  const semEmpresa = !integrador.isPending && !integrador.isError && linha == null;
  const mediaGravada = parseNum(linha?.projetos_mes) || null;
  // Sem cobertura não há histórico a gravar, e seguir gravaria `null` por cima
  // do que já está no banco. É esta trava que impede a visita de passagem de
  // apagar o volume de quem só veio olhar.
  const faltaEscolher = cobertura === null;

  return (
    <Pagina
      titulo="Sazonalidade"
      descricao="Quantos projetos a empresa vende por ano. É esse volume que rateia a despesa fixa."
      etapa="sazonalidade"
    >
      {integrador.isPending ? (
        <div className="favo">
          <Skeleton className="h-10 w-72 rounded-pilula" />
          <Skeleton className="h-72 w-full rounded-celula" />
        </div>
      ) : integrador.isError ? (
        <Falha
          oQue="a sazonalidade"
          motivo={mensagemDoErro(integrador.error)}
          aoTentarDeNovo={() => void integrador.refetch()}
        />
      ) : (
        <div className="favo">
          {semEmpresa ? (
            <Celula respiro="justo" className="bg-atencao-tinta text-apoio">
              Cadastre a empresa em{' '}
              <Link className="text-acao underline" to="/empresa">
                Empresa
              </Link>{' '}
              antes de informar o volume.
            </Celula>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Volume de vendas</CardTitle>
              <CardDescription>Mês a mês, mesmo que sejam poucos meses.</CardDescription>
            </CardHeader>

            <CardContent>
              <Historico
                cobertura={cobertura}
                aoMudarCobertura={setCobertura}
                meses={meses}
                aoMudarMes={mudarMes}
                resumo={resumo}
                mediaGravada={mediaGravada}
              />
            </CardContent>
          </Card>

          <RodapeFunil etapa="sazonalidade">
            <Button
              onClick={() => void enviar()}
              disabled={salvar.isPending || semEmpresa || faltaEscolher}
            >
              {salvar.isPending ? 'Salvando' : 'Continuar'}
              {salvar.isPending ? null : <ArrowRight />}
            </Button>
          </RodapeFunil>
        </div>
      )}
    </Pagina>
  );
}
